/**
 * Monitor Focus view: one row per wave (DEC-187 decisions 3 to 5, UI_SDD §R.3).
 *
 * Each row: chevron, "WAVE N", agents and findings, the wave's line, status on the right.
 * The line is the wave's findings by severity (live: raw counts from finding_produced;
 * saved: after review). A finished wave with none is a green line, a waiting wave an
 * empty track, a running wave its segments so far followed by moving stripes.
 *
 * Rows open on click and never close by themselves (DEC-093's reason was auto-collapse at
 * SSE speed). Running: every wave open. Finished: closed, except a wave with a failed or
 * skipped agent, which opens so its row is visible. Inside a row are today's agent
 * rows: pause state, round-decision Approve/Decline.
 *
 * DEC-197: an agent row shows only a short status (Failed, Timed out, Done with
 * problems, Skipped). The reason is never written under the row; a chevron marks the
 * row and a click opens the agent pop-up. DEC-195: each wave's run time sits in its
 * meta line.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowDownRight, ChevronDown, ChevronRight, MinusCircle } from 'lucide-react'
import { scansApi } from '../../api/scans'
import type { AgentState, SeverityCounts } from '../../stores/scanStore'
import { AGENT_CATEGORY_CODE, WAVE_AGENTS } from '../../types/api'
import { compactNumber, durationLabel } from '../../utils/format'
import {
  agentDescription,
  agentStatusWord,
  contextBadgeFor,
  DECISION_BORDER,
  DECISION_COLOR,
  DECISION_MARK,
  DECISION_TINT,
  displayColor,
  hasStory,
  isDone,
  PAUSED_BORDER,
  PAUSED_TINT,
  tokenColor,
} from './waveModel'
import { finishedWaveStatus, plural, runningSentence } from './SavedRunSummary'
import { TonePill, type PillTone } from '../shared/Badge'

interface FocusViewProps {
  agents: Record<string, AgentState>
  scanId: string
  isPaused: boolean
  /** DEC-185: rows rebuilt from the saved scan. A 'waiting' agent reads "Not run",
   * counts are after review, and time and tokens read "not recorded". */
  fromSaved?: boolean
  /** The scan is over (complete, partial or failed). Waves start closed then. */
  finished?: boolean
  /** DEC-195: each wave's run time. `seconds` null: not recorded. Absent: not known yet. */
  waveTimes?: Record<number, number | null>
  /** DEC-197: open the agent pop-up for a failed, problem, warned or skipped agent. */
  onInspect?: (agentId: string) => void
}

const SEVERITIES: (keyof SeverityCounts)[] = ['critical', 'high', 'medium', 'low']
// Severity bars are marks: the pill dot hues (DEC-193).
const SEV_BG: Record<keyof SeverityCounts, string> = {
  critical: 'bg-mark-critical',
  high: 'bg-mark-high',
  medium: 'bg-mark-medium',
  low: 'bg-mark-low',
}

// Moving stripes for a running wave's line. Scoped keyframes, so index.css is untouched.
// `--td-stripe` swaps to amber when the scan is paused (DEC-093), and the animation halts.
const STRIPE_CSS = `
@keyframes tdWaveStripes { from { background-position: 0 0; } to { background-position: 16px 0; } }
.td-wave-stripes {
  background-image: linear-gradient(45deg,
    rgb(var(--td-stripe, var(--accent-vivid)) / 0.6) 25%, rgb(var(--td-stripe, var(--accent-vivid)) / 0.18) 25%,
    rgb(var(--td-stripe, var(--accent-vivid)) / 0.18) 50%, rgb(var(--td-stripe, var(--accent-vivid)) / 0.6) 50%,
    rgb(var(--td-stripe, var(--accent-vivid)) / 0.6) 75%, rgb(var(--td-stripe, var(--accent-vivid)) / 0.18) 75%);
  background-size: 16px 16px;
  animation: tdWaveStripes .9s linear infinite;
}
.td-wave-stripes-paused { --td-stripe: var(--warn); animation-play-state: paused; }
`

interface WaveRowModel {
  num: number
  agents: AgentState[]
  status: 'waiting' | 'running' | 'done'
  paused: boolean
  /** Skipped counts as finished, the same as the banner's subtitle. */
  finishedCount: number
  total: number
  findings: number
  severity: SeverityCounts
  running: AgentState[]
  hasFailedOrSkipped: boolean
}

function buildWaveRows(
  agents: Record<string, AgentState>,
  isPaused: boolean,
  finished: boolean,
): WaveRowModel[] {
  return Object.keys(WAVE_AGENTS)
    .map(Number)
    .sort((a, b) => a - b)
    .map((num) => {
      const list = (WAVE_AGENTS[num] ?? []).map((id) => agents[id]).filter(Boolean)
      const nonSkipped = list.filter((a) => a.status !== 'skipped')
      const anyStarted = list.some((a) => a.status !== 'waiting' && a.status !== 'skipped')
      const allDone = nonSkipped.length === 0 || nonSkipped.every((a) => isDone(a.status))
      // A finished scan has no running wave: what did not run reads "not run".
      const status: WaveRowModel['status'] = finished
        ? 'done'
        : allDone && anyStarted
          ? 'done'
          : anyStarted
            ? 'running'
            : nonSkipped.length === 0
              ? 'done'
              : 'waiting'
      const severity: SeverityCounts = { critical: 0, high: 0, medium: 0, low: 0 }
      for (const a of list) for (const k of SEVERITIES) severity[k] += a.severity?.[k] ?? 0
      return {
        num,
        agents: list,
        status,
        paused: status === 'running' && isPaused,
        finishedCount: list.filter((a) => isDone(a.status)).length,
        total: list.length,
        findings: list.reduce((s, a) => s + a.findings_count, 0),
        severity,
        running: list.filter((a) => a.status === 'running'),
        hasFailedOrSkipped: list.some((a) => a.status === 'failed' || a.status === 'skipped' || a.status === 'done_with_problems'),
      }
    })
}

function WaveLine({ w }: { w: WaveRowModel }) {
  const sevTotal = SEVERITIES.reduce((s, k) => s + w.severity[k], 0)
  const segments = SEVERITIES.filter((k) => w.severity[k] > 0).map((k) => (
    <span
      key={k}
      data-testid={`wave-${w.num}-seg-${k}`}
      data-count={w.severity[k]}
      className={`${SEV_BG[k]} transition-[flex] duration-500`}
      style={{ flex: `${w.severity[k]} 1 0` }}
    />
  ))
  let body: ReactNode
  if (w.status === 'waiting') {
    body = null
  } else if (w.status === 'running') {
    // What it found so far, then stripes for what is still to come.
    const remaining = Math.max(w.total - w.finishedCount, 1)
    const stripeFlex = sevTotal === 0 ? 1 : Math.max((sevTotal * remaining) / Math.max(w.total, 1), sevTotal * 0.25)
    body = (
      <>
        {segments}
        <span
          data-testid={`wave-${w.num}-stripes`}
          className={`td-wave-stripes ${w.paused ? 'td-wave-stripes-paused' : ''}`}
          style={{ flex: `${stripeFlex} 1 0` }}
        />
      </>
    )
  } else if (sevTotal > 0) {
    body = segments
  } else if (w.findings > 0) {
    // Findings without a severity breakdown (a server before DEC-188): a neutral line,
    // never the green "nothing found" one.
    body = <span data-testid={`wave-${w.num}-seg-unknown`} className="bg-mark-idle" style={{ flex: '1 1 0' }} />
  } else {
    body = <span data-testid={`wave-${w.num}-seg-clean`} className="bg-mark-success" style={{ flex: '1 1 0' }} />
  }
  return (
    <span
      data-testid={`wave-${w.num}-line`}
      className="flex h-[5px] rounded-[3px] overflow-hidden gap-[2px] max-w-[520px] bg-fill-subtle"
    >
      {body}
    </span>
  )
}

export function FocusView({
  agents,
  scanId,
  isPaused,
  fromSaved = false,
  finished = false,
  waveTimes = {},
  onInspect,
}: FocusViewProps) {
  const navigate = useNavigate()
  const isFinished = finished || fromSaved
  const waves = buildWaveRows(agents, isPaused, isFinished)
  const [submittingAgent, setSubmittingAgent] = useState<string | null>(null)
  // Explicit open state per wave. A default "open" is latched into it, so a wave opened
  // while running stays open when the scan finishes: rows never close by themselves.
  const [open, setOpen] = useState<Record<number, boolean>>({})
  const defaultOpen = (w: WaveRowModel) => (isFinished ? w.hasFailedOrSkipped : true)
  const latchKey = waves.map((w) => (defaultOpen(w) ? '1' : '0')).join('')

  useEffect(() => {
    setOpen((current) => {
      let next = current
      for (const w of waves) {
        if (defaultOpen(w) && current[w.num] === undefined) {
          if (next === current) next = { ...current }
          next[w.num] = true
        }
      }
      return next
    })
    // latchKey captures every input of defaultOpen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latchKey])

  function submitDecision(agentId: string, approve: boolean) {
    setSubmittingAgent(agentId)
    scansApi
      .submitRoundDecision(scanId, agentId, approve)
      .catch(() => {
        /* SSE + a later manual retry are the recovery path; nothing to show inline here */
      })
      .finally(() => setSubmittingAgent((current) => (current === agentId ? null : current)))
  }

  return (
    <div className="bg-panel border border-hairline rounded-lg px-6 py-[6px]">
      <style>{STRIPE_CSS}</style>
      {waves.map((w) => {
        const isOpen = open[w.num] ?? defaultOpen(w)
        const running = w.status === 'running'
        const fin = w.status === 'done' ? finishedWaveStatus(w.agents) : null
        const statusText = running
          ? `${w.paused ? 'Paused' : 'Running'} · ${w.finishedCount} of ${w.total}`
          : fin
            ? fin.text
            : 'Waiting'
        // The one pill style (DEC-193 decision 2) for every wave's status.
        const statusTone: PillTone = running ? (w.paused ? 'warn' : 'running') : fin ? fin.tone : 'waiting'
        const sentence = running
          ? [runningSentence(w.running.map((a) => a.label), w.paused), `${w.finishedCount} of ${w.total} agents done`]
              .filter(Boolean)
              .join(' · ')
          : null
        return (
          <div
            key={w.num}
            data-testid={`wave-row-${w.num}`}
            data-status={w.status}
            data-open={isOpen ? 'true' : 'false'}
            className={`border-b border-hairline last:border-b-0 transition-opacity ${
              running ? 'rounded-lg -mx-4 px-4 my-[6px]' : ''
            } ${w.status === 'waiting' ? 'opacity-[.55]' : ''}`}
            style={
              running
                ? {
                    // DEC-187 decision 4: gradient and a soft outline, never a left bar.
                    background: w.paused
                      ? `linear-gradient(90deg, ${PAUSED_TINT}, transparent)`
                      : 'linear-gradient(90deg, rgb(var(--accent-vivid) / 0.22), rgb(var(--accent-vivid) / 0.05))',
                    boxShadow: w.paused
                      ? `0 0 0 1px ${PAUSED_BORDER}`
                      : '0 0 0 1px rgb(var(--accent-text) / 0.35), 0 0 22px rgb(var(--accent-vivid) / 0.14)',
                  }
                : undefined
            }
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpen((cur) => ({ ...cur, [w.num]: !isOpen }))}
              className="w-full bg-transparent border-0 text-left cursor-pointer grid grid-cols-[18px_96px_minmax(0,1fr)_auto] gap-[18px] py-4 items-center"
            >
              <span className="text-muted">
                {isOpen ? <ChevronDown className="w-[14px] h-[14px]" /> : <ChevronRight className="w-[14px] h-[14px]" />}
              </span>
              <span className="inline-flex items-center gap-2">
                {running && (
                  <span
                    data-testid={`wave-${w.num}-pulse`}
                    className="w-2 h-2 rounded-full flex-none"
                    style={{
                      background: tokenColor(w.paused ? 'mark-warn' : 'mark-running'),
                      boxShadow: `0 0 0 4px ${tokenColor(w.paused ? 'mark-warn' : 'mark-running', 0.25)}`,
                      animation: w.paused ? 'none' : 'specPulse 1.4s ease-in-out infinite',
                    }}
                  />
                )}
                <span className="font-semibold text-[12px] leading-none tracking-[0.16em] uppercase text-ink whitespace-nowrap">
                  Wave {w.num}
                </span>
              </span>
              <span className="flex flex-col gap-2 min-w-0">
                <span data-testid={`wave-${w.num}-summary`} className={`text-[14px] leading-[1.45] ${running ? 'text-ink' : 'text-body'}`}>
                  {w.agents.map((a) => a.label).join(', ')}
                  {' · '}
                  {plural(w.findings, 'finding')}
                  {fromSaved ? ' after review' : ''}
                  {typeof waveTimes[w.num] === 'number' && w.status !== 'waiting' && (
                    <span data-testid={`wave-${w.num}-time`} className="text-muted tabular-nums">
                      {' · '}
                      {durationLabel(waveTimes[w.num] as number)}
                    </span>
                  )}
                </span>
                <WaveLine w={w} />
                {sentence && (
                  <span data-testid={`wave-${w.num}-sentence`} className="text-[12.5px] leading-[1.4] text-accent-text">
                    {sentence}
                  </span>
                )}
              </span>
              <TonePill tone={statusTone} pulse={running && !w.paused} testId={`wave-${w.num}-status`}>
                {statusText}
              </TonePill>
            </button>

            {isOpen && (
              <div data-testid={`wave-${w.num}-agents`} className="flex flex-col gap-2 pb-4 pl-[132px] max-md:pl-0">
                {w.agents.map((a) => (
                  <AgentRow
                    key={a.agent_id}
                    a={a}
                    agents={agents}
                    scanId={scanId}
                    isPaused={isPaused}
                    fromSaved={fromSaved}
                    submitting={submittingAgent === a.agent_id}
                    onDecision={submitDecision}
                    onOpen={(path) => navigate(path)}
                    onInspect={onInspect}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface AgentRowProps {
  a: AgentState
  agents: Record<string, AgentState>
  scanId: string
  isPaused: boolean
  fromSaved: boolean
  submitting: boolean
  onDecision: (agentId: string, approve: boolean) => void
  onOpen: (path: string) => void
  onInspect?: (agentId: string) => void
}

/** Today's agent row (DEC-093, DEC-094, DEC-146), unchanged apart from the running
 *  highlight, which matches the running wave: gradient and soft outline, no left bar. */
function AgentRow({ a, agents, scanId, isPaused, fromSaved, submitting, onDecision, onOpen, onInspect }: AgentRowProps) {
  const ctx = contextBadgeFor(a.agent_id, agents)
  // DEC-197: a row with something to explain opens the pop-up; a clean one, its findings.
  const story = hasStory(a) && !!onInspect
  const toFindings = a.status === 'complete' && !!scanId
  const clickable = story || toFindings
  const category = AGENT_CATEGORY_CODE[a.agent_id]
  const running = a.status === 'running'
  const runningPaused = running && isPaused
  const decisionPending = running && a.roundDecisionPending
  const color = decisionPending ? DECISION_COLOR : displayColor(a.status, isPaused)
  return (
    <div data-testid={`agent-row-${a.agent_id}`} data-running={running ? 'true' : undefined}>
      <div
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={story ? `${a.label}, ${agentStatusWord(a, isPaused)}. Open what happened` : undefined}
        aria-haspopup={story ? 'dialog' : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  ;(e.currentTarget as HTMLElement).click()
                }
              }
            : undefined
        }
        onClick={
          story
            ? () => onInspect!(a.agent_id)
            : toFindings
              ? () => onOpen(category ? `/scans/${scanId}/findings?category=${category}` : `/scans/${scanId}/findings`)
              : undefined
        }
        className={`flex items-center gap-3 border rounded-md px-[13px] py-[11px] transition-colors ${
          clickable ? 'cursor-pointer hover:border-hover-edge' : ''
        } ${a.status === 'skipped' ? 'opacity-55' : ''}`}
        style={{
          background: decisionPending
            ? DECISION_TINT
            : running
              ? runningPaused
                ? PAUSED_TINT
                : 'linear-gradient(90deg, rgb(var(--accent-vivid) / 0.2), rgb(var(--accent-vivid) / 0.05))'
              : '',
          borderColor: decisionPending
            ? DECISION_BORDER
            : running
              ? runningPaused
                ? PAUSED_BORDER
                : 'rgb(var(--accent-text) / 0.35)'
              : 'var(--fill-subtle)',
          boxShadow: running && !runningPaused && !decisionPending ? '0 0 16px rgb(var(--accent-vivid) / 0.14)' : undefined,
        }}
      >
        <span
          className="w-2 h-2 rounded-full flex-none"
          style={{
            background: color,
            animation: running && !runningPaused && !decisionPending ? 'specPulse 1.4s ease infinite' : 'none',
          }}
        />
        <div className="w-32 flex-none">
          <div className="font-semibold text-[12.5px] leading-none">{a.label}</div>
          {ctx && (
            <div className="flex items-center gap-1 mt-[5px]">
              {ctx.carried ? (
                <ArrowDownRight className="w-[10px] h-[10px] text-accent-text flex-none" />
              ) : (
                <MinusCircle className="w-[10px] h-[10px] text-faint flex-none" />
              )}
              <span className={`font-medium text-[8.5px] leading-tight ${ctx.carried ? 'text-accent-text' : 'text-faint'}`}>
                {ctx.text}
              </span>
            </div>
          )}
        </div>
        <span className="text-[11px] leading-[1.3] text-muted flex-1 min-w-0">{agentDescription(a.agent_id)}</span>
        {decisionPending ? (
          <div className="flex items-center gap-[7px] flex-none">
            <span className="font-semibold text-[9px] leading-none uppercase tracking-[0.5px]" style={{ color: DECISION_COLOR }}>
              Waiting on you
            </span>
            <button
              type="button"
              disabled={submitting}
              onClick={(e) => {
                e.stopPropagation()
                onDecision(a.agent_id, true)
              }}
              className="font-semibold text-[9px] leading-none uppercase tracking-[0.5px] text-white px-2 py-[4px] rounded-md disabled:opacity-50"
              style={{ background: DECISION_MARK }}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={(e) => {
                e.stopPropagation()
                onDecision(a.agent_id, false)
              }}
              className="font-semibold text-[9px] leading-none uppercase tracking-[0.5px] px-2 py-[4px] rounded-md border disabled:opacity-50"
              style={{ borderColor: DECISION_BORDER, color: DECISION_COLOR }}
            >
              Decline
            </button>
          </div>
        ) : (
          <>
            {running ? (
              <div
                className="w-[120px] h-[3px] rounded-full flex-none overflow-hidden"
                style={{ background: runningPaused ? tokenColor('warn', 0.18) : 'rgb(var(--accent) / 0.18)' }}
              >
                {!runningPaused && (
                  <div
                    className="h-full w-1/3 rounded-full animate-[tdSlide_1.2s_ease-in-out_infinite]"
                    style={{ background: tokenColor('mark-running') }}
                  />
                )}
              </div>
            ) : (
              <>
                <span
                  className="font-semibold tabular-nums text-[11px] leading-none w-[34px] text-right flex-none"
                  style={{ color: a.findings_count > 0 ? 'rgb(var(--ink))' : 'rgb(var(--faint))' }}
                >
                  {isDone(a.status) && a.status !== 'skipped' ? a.findings_count : '–'}
                </span>
                <span className="tabular-nums text-[10px] leading-none text-faint w-[86px] text-right flex-none">
                  {isDone(a.status) && a.status !== 'skipped'
                    ? fromSaved && !a.timeRecorded
                      ? 'not recorded'
                      : `${compactNumber(a.tokens_used)} · ${durationLabel(a.duration_seconds)}`
                    : ''}
                </span>
              </>
            )}
            <span
              data-testid={`agent-status-${a.agent_id}`}
              className="font-semibold text-[9px] leading-none uppercase tracking-[0.5px] min-w-[62px] text-right flex-none whitespace-nowrap"
              style={{ color }}
            >
              {fromSaved && a.status === 'waiting' ? 'Not run' : agentStatusWord(a, isPaused)}
            </span>
            {story && <ChevronRight aria-hidden className="w-3 h-3 text-muted flex-none -ml-1" />}
          </>
        )}
      </div>
    </div>
  )
}
